import { supabase } from '@/lib/supabase';
import {
  buildCashierDay,
  type CashierDayDetail,
  type CashierDayInvoice,
  type CashierDayLine,
  type CashierDayPayment,
  type CashierDayVisit,
} from './cashierDay';
import type { CashierFilterState, CashierPaymentRow } from './cashierTypes';

interface PatientEmbed {
  first_name?: string | null;
  last_name?: string | null;
}

interface ClinicianEmbed {
  name?: string | null;
}

function personName(first: unknown, last: unknown): string | null {
  const name = [first, last]
    .filter((part) => typeof part === 'string' && part.trim())
    .join(' ')
    .trim();
  return name || null;
}

function one<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

const INVOICE_COLUMNS =
  'id, invoice_number, kind, status, total_cents, total_paid_cents, issued_at, due_at, appointment_id, credits_invoice_id, clinic_id, patient:patients(first_name, last_name)';

function toInvoice(row: {
  id: string;
  invoice_number: string | null;
  kind: string | null;
  status: string;
  total_cents: number;
  total_paid_cents: number | null;
  issued_at: string | null;
  due_at: string | null;
  appointment_id: string | null;
  credits_invoice_id: string | null;
  clinic_id: string;
  patient: PatientEmbed | PatientEmbed[] | null;
}, inPeriod: boolean): CashierDayInvoice {
  const patient = one(row.patient);
  return {
    id: row.id,
    invoiceNumber: row.invoice_number,
    kind: row.kind,
    status: row.status,
    totalCents: row.total_cents,
    paidCents: row.total_paid_cents ?? 0,
    issuedAt: row.issued_at,
    dueAt: row.due_at,
    appointmentId: row.appointment_id,
    creditsInvoiceId: row.credits_invoice_id,
    patientName: personName(patient?.first_name, patient?.last_name),
    clinicId: row.clinic_id,
    inPeriod,
  };
}

export async function loadCashierDay(
  clinicId: string,
  range: { start: string; end: string },
  filters: CashierFilterState,
  transactions: CashierPaymentRow[],
): Promise<CashierDayDetail> {
  const clinicIds = filters.locationIds.length > 0 ? filters.locationIds : [clinicId];
  let visitsQuery = supabase
    .from('appointments')
    .select(
      'id, clinician_id, service_name, completed_at, patient:patients(first_name, last_name), clinician:users!appointments_clinician_id_fkey(name)',
    )
    .in('clinic_id', clinicIds)
    .eq('status', 'completed')
    .gte('completed_at', range.start)
    .lte('completed_at', range.end);
  if (filters.practitionerIds.length > 0) {
    visitsQuery = visitsQuery.in('clinician_id', filters.practitionerIds);
  }
  const { data: visitRows, error: visitError } = await visitsQuery;
  if (visitError) throw visitError;

  const visits: CashierDayVisit[] = (visitRows ?? []).map((row) => {
    const patient = one(row.patient as PatientEmbed | PatientEmbed[] | null);
    const clinician = one(row.clinician as ClinicianEmbed | ClinicianEmbed[] | null);
    return {
      id: row.id,
      clinicianId: row.clinician_id,
      clinicianName: clinician?.name ?? null,
      serviceName: row.service_name,
      patientName: personName(patient?.first_name, patient?.last_name),
      completedAt: row.completed_at,
    };
  });

  const { data: issuedRows, error: issuedError } = await supabase
    .from('invoices')
    .select(INVOICE_COLUMNS)
    .in('clinic_id', clinicIds)
    .gte('issued_at', range.start)
    .lte('issued_at', range.end);
  if (issuedError) throw issuedError;

  const visitIds = visits.map((visit) => visit.id);
  let linkedRows: typeof issuedRows = [];
  if (visitIds.length > 0) {
    const { data, error } = await supabase.from('invoices').select(INVOICE_COLUMNS).in('appointment_id', visitIds);
    if (error) throw error;
    linkedRows = data ?? [];
  }

  const invoiceMap = new Map<string, CashierDayInvoice>();
  for (const row of issuedRows ?? []) invoiceMap.set(row.id, toInvoice(row, true));
  for (const row of linkedRows ?? []) {
    if (!invoiceMap.has(row.id)) invoiceMap.set(row.id, toInvoice(row, false));
  }

  const originalIds = [...invoiceMap.values()]
    .map((invoice) => invoice.creditsInvoiceId)
    .filter((id): id is string => Boolean(id && !invoiceMap.has(id)));
  if (originalIds.length > 0) {
    const { data, error } = await supabase.from('invoices').select(INVOICE_COLUMNS).in('id', originalIds);
    if (error) throw error;
    for (const row of data ?? []) {
      if (!invoiceMap.has(row.id)) invoiceMap.set(row.id, toInvoice(row, false));
    }
  }

  const invoiceIds = [...invoiceMap.keys()];
  const lines: CashierDayLine[] = [];
  const payments: CashierDayPayment[] = [];
  if (invoiceIds.length > 0) {
    const [lineResult, paymentResult] = await Promise.all([
      supabase
        .from('invoice_line_items')
        .select('invoice_id, description, line_total_cents, source')
        .in('invoice_id', invoiceIds),
      supabase.from('invoice_payments').select('id, invoice_id, amount_cents, created_at').in('invoice_id', invoiceIds),
    ]);
    if (lineResult.error) throw lineResult.error;
    if (paymentResult.error) throw paymentResult.error;
    for (const line of lineResult.data ?? []) {
      lines.push({
        invoiceId: line.invoice_id,
        description: line.description,
        lineTotalCents: line.line_total_cents,
        source: line.source,
      });
    }
    for (const payment of paymentResult.data ?? []) {
      payments.push({
        id: payment.id,
        invoiceId: payment.invoice_id,
        amountCents: payment.amount_cents,
        createdAt: payment.created_at,
      });
    }
  }

  const recordedAtByPaymentId: Record<string, string> = {};
  for (const payment of payments) {
    if (payment.createdAt) recordedAtByPaymentId[payment.id] = payment.createdAt;
  }
  const missingTransactionIds = transactions.map((row) => row.id).filter((id) => !recordedAtByPaymentId[id]);
  if (missingTransactionIds.length > 0) {
    const { data, error } = await supabase
      .from('invoice_payments')
      .select('id, created_at')
      .in('id', missingTransactionIds);
    if (error) throw error;
    for (const row of data ?? []) {
      if (row.created_at) recordedAtByPaymentId[row.id] = row.created_at;
    }
  }

  return buildCashierDay(
    { visits, invoices: [...invoiceMap.values()], lines, payments, recordedAtByPaymentId },
    filters,
  );
}
